import { Injectable } from '@angular/core';
import { Accommodation as AccommodationAPI, GroupableAccommodation, OTHER } from '@lx/shared/data-access/products';
import { Accommodation, HOMESTAY } from '../types';
import { deepClone } from '@lx/shared/utils';
import { sortArray } from '@lx/shared/utils-array';

@Injectable({
  providedIn: 'root',
})
export class AccommodationsService {
  private readonly otherAccommodationOption = { type: OTHER, code: null };

  getOtherAccommodationOption(): Accommodation {
    return this.otherAccommodationOption;
  }

  /**
   * Gets the list of accommodations, compacted them by name and sort them by price (ascending).
   * If the selected accommodation is homestay or 'I don't need accommodation,' then make Homestay the first option in the list.
   * @param accommodations
   * @param selectedAccommodation
   */
  getCompactedAndSortedAccommodations(accommodations: AccommodationAPI[], selectedAccommodation: Accommodation): GroupableAccommodation[] {
    const priceAvailable = accommodations.length > 0 && accommodations[0].price?.value !== undefined;
    const groupedAccommodationsByName = this.getGroupedAccommodationsByName(accommodations, priceAvailable);
    const cheapestHomestay = groupedAccommodationsByName.filter(this.isHomeStay)[0];

    if (priceAvailable) {
      return this.getSortedAccommodationsByPrice(selectedAccommodation, cheapestHomestay, groupedAccommodationsByName);
    } else {
      return sortArray(groupedAccommodationsByName, [{ property: 'accommodationType' }]);
    }
  }

  private getSortedAccommodationsByPrice(
    selectedAccommodation: Accommodation,
    cheapestHomestay: GroupableAccommodation,
    accommodations: GroupableAccommodation[],
  ): GroupableAccommodation[] {
    // Filter out homestays first
    const updatedAccommodations = accommodations.filter((acc) => !this.isHomeStay(acc)).sort(this.sortCheapestToMostExpensive);

    // If the selected accommodation is homestay or 'I don't need accommodation,' then make Homestay the first option in the list
    if (selectedAccommodation.type === HOMESTAY || selectedAccommodation.code === null) {
      updatedAccommodations.unshift(cheapestHomestay);
    } else {
      updatedAccommodations.push(cheapestHomestay);
    }

    return updatedAccommodations;
  }

  private sortCheapestToMostExpensive(accommodationA: AccommodationAPI, accommodationB: AccommodationAPI): number {
    return accommodationA.price.value - accommodationB.price.value;
  }

  private isHomeStay(accommodation: AccommodationAPI): boolean {
    return accommodation.accommodationType === HOMESTAY;
  }

  private getGroupedAccommodationsByName(accommodations: AccommodationAPI[], priceAvailable: boolean): GroupableAccommodation[] {
    return Object.values(
      accommodations.reduce((acc: Record<string, GroupableAccommodation>, item: AccommodationAPI) => {
        const groupedItems = acc[item.name]?.groupedItems || [];

        if (!acc[item.name] || !priceAvailable || (item.price?.value ?? Number.MAX_SAFE_INTEGER) < (acc[item.name].price?.value ?? Number.MAX_SAFE_INTEGER)) {
          acc[item.name] = {
            ...deepClone(item),
            groupingKey: item.name?.toLowerCase(),
            galleryUrls: item.galleryUrls?.length ? [...item.galleryUrls] : [],
            groupedItems,
          };
        }

        acc[item.name].groupedItems = [...groupedItems, item];

        return acc;
      }, {}),
    );
  }
}
